import { AppBar, Box, List, ListItem, ListItemText, ListSubheader, Toolbar, Typography } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import React, { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { resolveRoutePath, SiteShellProps } from 'src/config/sites/SiteDefinition';
import { useEnabledSiteModuleKeys } from 'src/core/provider/SiteProvider';

const useStyles = makeStyles(theme =>
    createStyles({
        root: {
            display: 'flex',
            minHeight: '100vh',
            backgroundColor: theme.palette.background.default,
        },
        nav: {
            width: 280,
            background: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            display: 'flex',
            flexDirection: 'column',
        },
        navHeader: {
            padding: theme.spacing(4, 3, 2),
        },
        navDescription: {
            marginTop: theme.spacing(1),
            opacity: 0.8,
        },
        navList: {
            paddingTop: theme.spacing(2),
            paddingBottom: theme.spacing(2),
            flex: '1 1 auto',
            overflowY: 'auto',
        },
        navLink: {
            textDecoration: 'none',
            color: 'inherit',
            display: 'block',
        },
        navLinkActive: {
            backgroundColor: theme.palette.primary.dark,
        },
        content: {
            flex: '1 1 auto',
            display: 'flex',
            flexDirection: 'column',
            background: theme.palette.background.default,
        },
        main: {
            flex: '1 1 auto',
            padding: theme.spacing(4),
        },
        emptyNav: {
            padding: theme.spacing(2, 3),
            opacity: 0.85,
        },
    })
);

const ShowcaseSiteShell: React.FC<SiteShellProps> = ({ site, children }) => {
    const classes = useStyles();
    const enabledModules = useEnabledSiteModuleKeys();

    const enabledRoutePaths = useMemo(() => {
        const enabledPaths = site.routes
            .filter(route => enabledModules.has(route.key))
            .map(route => resolveRoutePath(route.route));
        return new Set(enabledPaths);
    }, [enabledModules, site]);

    const filteredNavigation = useMemo(
        () =>
            (site.navigation ?? [])
                .map(section => ({
                    ...section,
                    items: section.items.filter(item => enabledRoutePaths.has(resolveRoutePath(item.route))),
                }))
                .filter(section => section.items.length > 0),
        [enabledRoutePaths, site.navigation]
    );

    return (
        <Box className={classes.root}>
            <Box component="aside" className={classes.nav}>
                <div className={classes.navHeader}>
                    <Typography variant="h6">{site.label}</Typography>
                    {site.description && (
                        <Typography variant="body2" className={classes.navDescription}>
                            {site.description}
                        </Typography>
                    )}
                </div>

                {filteredNavigation.length > 0 ? (
                    <div className={classes.navList}>
                        {filteredNavigation.map(section => (
                            <List
                                key={section.id}
                                component="nav"
                                aria-label={section.label}
                                subheader={
                                    <ListSubheader component="div" disableSticky>
                                        {section.label}
                                    </ListSubheader>
                                }>
                                {section.items.map(item => (
                                    <NavLink
                                        key={item.id}
                                        to={resolveRoutePath(item.route)}
                                        exact
                                        className={classes.navLink}
                                        activeClassName={classes.navLinkActive}>
                                        <ListItem button>
                                            <ListItemText primary={item.label} />
                                        </ListItem>
                                    </NavLink>
                                ))}
                            </List>
                        ))}
                    </div>
                ) : (
                    <Typography variant="body2" className={classes.emptyNav}>
                        Navigation is not configured for this site.
                    </Typography>
                )}
            </Box>

            <Box className={classes.content}>
                <AppBar position="static" color="transparent" elevation={0}>
                    <Toolbar>
                        <Typography variant="h6">{site.label}</Typography>
                    </Toolbar>
                </AppBar>
                <Box component="main" className={classes.main}>
                    {children}
                </Box>
            </Box>
        </Box>
    );
};

export default ShowcaseSiteShell;
